//frontend/src/components/dashboard/AppointmentsManager.js
import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Tabs,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Typography,
    Tooltip,
    styled,
    alpha,
    Chip
} from '@mui/material';
import {
    Delete as DeleteIcon,
    Edit as EditIcon,
    Person as PersonIcon,
} from '@mui/icons-material';
import { format, isToday, isFuture, isPast, isWithinInterval, addMinutes } from 'date-fns';
import axiosInstance from '../../utils/axios';
import { useTranslation } from 'react-i18next';

// Styled components for blinking effects
const AppointmentRow = styled(TableRow)(({ status }) => {
    const getBackgroundColor = () => {
        switch (status) {
            case 'upcoming':
                return alpha('#4caf50', 0.1); // Light green
            case 'ongoing':
                return alpha('#ff9800', 0.1); // Light orange
            case 'past':
                return alpha('#f44336', 0.1); // Light red
            default:
                return 'transparent';
        }
    };

    const getAnimation = () => {
        switch (status) {
            case 'upcoming':
                return 'blinkGreen 3s infinite';
            case 'ongoing':
                return 'blinkOrange 2s infinite';
            default:
                return 'none';
        }
    };

    return {
        backgroundColor: getBackgroundColor(),
        animation: getAnimation(),
        '@keyframes blinkGreen': {
            '0%': { backgroundColor: 'transparent' },
            '50%': { backgroundColor: alpha('#4caf50', 0.1) },
            '100%': { backgroundColor: 'transparent' },
        },
        '@keyframes blinkOrange': {
            '0%': { backgroundColor: 'transparent' },
            '50%': { backgroundColor: alpha('#ff9800', 0.1) },
            '100%': { backgroundColor: 'transparent' },
        },
    };
});

export default function AppointmentsManager({ isSuperAdmin }) {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState(0);
    const [appointments, setAppointments] = useState([]);
    const [specialists, setSpecialists] = useState([]);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [selectedAppointment, setSelectedAppointment] = useState(null);
    const [selectedSpecialist, setSelectedSpecialist] = useState('');

    useEffect(() => {
        fetchAppointments();
        if (isSuperAdmin) {
            fetchSpecialists();
        }
    }, [isSuperAdmin]);


    const getTranslatedStatus = (status) => {
        return t(`common.${status}`);
    };

    const fetchAppointments = async () => {
        try {
            const response = await axiosInstance.get('/appointments');
            const sortedAppointments = response.data.sort((a, b) => 
                new Date(a.startTime) - new Date(b.startTime)
            );
            setAppointments(sortedAppointments);
        } catch (error) {
            console.error('Error fetching appointments:', error);
        }
    };

    const fetchSpecialists = async () => {
        try {
            const response = await axiosInstance.get('/specialists');
            setSpecialists(response.data);
        } catch (error) {
            console.error('Error fetching specialists:', error);
        }
    };

    const getAppointmentStatus = (startTime, duration) => {
        const start = new Date(startTime);
        const end = addMinutes(start, duration);
        const now = new Date();

        if (isPast(end)) return 'past';
        if (isWithinInterval(now, { start, end })) return 'ongoing';
        if (isToday(start) && isFuture(start)) return 'upcoming';
        return 'future';
    };

    const handleDeleteAppointment = async (id) => {
        if (window.confirm(t('dashboard.appointments.deleteConfirm'))) {
            try {
                await axiosInstance.delete(`/appointments/${id}`);
                fetchAppointments();
            } catch (error) {
                console.error('Error deleting appointment:', error);
            }
        }
    };

    const handleEditClick = (appointment) => {
        setSelectedAppointment(appointment);
        setSelectedSpecialist(appointment.specialistId);
        setEditDialogOpen(true);
    };

    const handleEditSave = async () => {
        try {
            await axiosInstance.patch(`/appointments/${selectedAppointment._id}/specialist`, {
                specialistId: selectedSpecialist
            });
            fetchAppointments();
            setEditDialogOpen(false);
        } catch (error) {
            console.error('Error updating appointment:', error);
        }
    };

    const filterAppointments = () => {
        const currentDate = new Date();
        return appointments.filter(appointment => {
            const appointmentDate = new Date(appointment.startTime);
            return activeTab === 0 
                ? !isPast(addMinutes(appointmentDate, appointment.duration))
                : isPast(addMinutes(appointmentDate, appointment.duration));
        });
    };

    return (
        <Box>
            <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)} sx={{ mb: 2 }}>
                <Tab label={t('dashboard.appointments.upcoming')} />
                <Tab label={t('dashboard.appointments.past')} />
            </Tabs>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>{t('dashboard.appointments.datetime')}</TableCell>
                            <TableCell>{t('dashboard.appointments.client')}</TableCell>
                            <TableCell>{t('dashboard.appointments.service')}</TableCell>
                            <TableCell>{t('dashboard.appointments.specialist')}</TableCell>
                            <TableCell>{t('dashboard.appointments.duration')}</TableCell>
                            <TableCell>{t('common.status')}</TableCell>
                            <TableCell>{t('common.actions')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filterAppointments().map((appointment) => {
                            const status = getAppointmentStatus(
                                appointment.startTime, 
                                appointment.duration
                            );
                            
                            return (
                                <AppointmentRow key={appointment._id} status={status}>
                                    <TableCell>
                                        {format(new Date(appointment.startTime), 'dd.MM.yyyy HH:mm')}
                                    </TableCell>
                                    <TableCell>
                                        {appointment.clientInfo.name}
                                        <br />
                                        <Typography variant="caption" color="textSecondary">
                                            {appointment.clientInfo.phone}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>{appointment.service}</TableCell>
                                    <TableCell>{appointment.specialistId?.name}</TableCell>
                                    <TableCell>{appointment.duration} {t('common.min')}</TableCell>
                                    <TableCell>
                                    <Chip 
    label={getTranslatedStatus(status)} 
    color={
        status === 'upcoming' ? 'success' :
        status === 'ongoing' ? 'warning' :
        status === 'past' ? 'error' : 'default'
    }
    size="small"
/>
                                    </TableCell>
                                    <TableCell>
                                        {isSuperAdmin && (
                                            <>
                                                <Tooltip title={t('common.edit')}>
    <IconButton 
        size="small" 
        onClick={() => handleEditClick(appointment)}
    >
        <PersonIcon />
    </IconButton>
</Tooltip>
<Tooltip title={t('common.delete')}>
    <IconButton
        size="small"
        color="error"
        onClick={() => handleDeleteAppointment(appointment._id)}
    >
        <DeleteIcon />
    </IconButton>
</Tooltip>
                                            </>
                                        )}
                                    </TableCell>
                                </AppointmentRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)}>
                <DialogTitle>{t('common.changespecialist')}</DialogTitle>
                <DialogContent>
                    <FormControl fullWidth sx={{ mt: 2 }}>
                        <InputLabel>{t('common.specialist')}</InputLabel>
                        <Select
                            value={selectedSpecialist}
                            onChange={(e) => setSelectedSpecialist(e.target.value)}
                        >
                            {specialists.map((specialist) => (
                                <MenuItem key={specialist._id} value={specialist._id}>
                                    {specialist.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditDialogOpen(false)}>{t('common.cancel')}</Button>
                    <Button onClick={handleEditSave} variant="contained">
                    {t('common.savechanges')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}